/**
 * Strict ISO 3779 VIN pattern: exactly 17 characters using digits and uppercase
 * letters, excluding I, O and Q (to avoid confusion with 1 and 0). Format only.
 */
export const VIN_REGEX = /^[A-HJ-NPR-Z0-9]{17}$/;
const VIN_DISALLOWED_CHAR_REGEX = /[^A-HJ-NPR-Z0-9]/;
export const VEHICLE_VIN_LENGTH = 17;

export const normalizeVehicleVin = (value: string): string => {
  return value.trim().toUpperCase();
};

/**
 * Visual status of a VIN field, independent of which errors are surfaced:
 * - `Idle`: empty field, no feedback yet.
 * - `Incomplete`: partially typed and still error-free.
 * - `Valid`: a complete, well-formed VIN.
 * - `Invalid`: a character and/or length error is present.
 */
export enum VehicleVinFieldStatus {
  Idle = "Idle",
  Incomplete = "Incomplete",
  Valid = "Valid",
  Invalid = "Invalid",
}

export interface VehicleVinValidation {
  status: VehicleVinFieldStatus;
  length: number;
  hasCharacterError: boolean;
  hasLengthError: boolean;
  hasError: boolean;
  isValid: boolean;
}

export const getVehicleVinValidation = (value: string, validateLength: boolean): VehicleVinValidation => {
  const normalized = normalizeVehicleVin(value);
  const length = normalized.length;

  const hasCharacterError = VIN_DISALLOWED_CHAR_REGEX.test(normalized);
  const isValid = !hasCharacterError && length === VEHICLE_VIN_LENGTH;

  const hasLengthError = validateLength && length > 0 && length !== VEHICLE_VIN_LENGTH;
  const hasError = hasCharacterError || hasLengthError;

  const status = hasError
    ? VehicleVinFieldStatus.Invalid
    : isValid
      ? VehicleVinFieldStatus.Valid
      : length === 0
        ? VehicleVinFieldStatus.Idle
        : VehicleVinFieldStatus.Incomplete;

  return { status, length, hasCharacterError, hasLengthError, hasError, isValid };
};
